import React, { useContext } from 'react';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import ImageComponent from 'shared/ImageComponent';
import { paginationDropdownOptions } from 'utils/utils';
import { UserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import { Dropdown } from 'primereact/dropdown';
import { handlePageNumberInPagination } from 'common/pagination';
import { truncateText } from 'utils/utility_functions/textTruncate';
import { generateFullName } from 'utils/generateFullNameUtils';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { Link } from 'react-router-dom/cjs/react-router-dom';

function ActivityLogTable(props) {
  const {
    activityLog,
    isCaseSpecific,
    isLead,
    isFromDashBoard,
    isFromTeamManagement = false,
    isFromReports = false,
    pageLimit,
    onPageLimitChange,
    paginatorTemplate: paginatorTemplateProp,
  } = props;
  const userContext = useContext(UserDetailsContext);

  const rowsPerPage = pageLimit ?? 10;
  const emptyDataValue = '-';

  const dateTimeTemplate = (rowData) => {
    if (rowData && rowData.timestamp) {
      return (
        <>
          <span className="p-column-title text-break">Date and Time</span>
          <div className="d-block">
            <div
              title={`${convertDateFromApiToUserTimezone(
                rowData.timestamp,
                userContext?.userDetails?.timezone,
                constants.month_date_year_format
              )}`}
            >{`${convertDateFromApiToUserTimezone(
              rowData.timestamp,
              userContext?.userDetails?.timezone,
              constants.month_date_year_format
            )}`}</div>
            <div
              title={`${convertDateFromApiToUserTimezone(
                rowData.timestamp,
                userContext?.userDetails?.timezone,
                constants.hour_minute_12_format
              )}`}
            >
              {`${convertDateFromApiToUserTimezone(
                rowData.timestamp,
                userContext?.userDetails?.timezone,
                constants.hour_minute_12_format
              )}`}
            </div>
          </div>
        </>
      );
    } else {
      return 'N/A';
    }
  };

  const userTemplate = (rowData) => {
    const userId = rowData?.items?.firm_user_id || rowData?.items?.contact_id;
    return (
      <>
        <span className="p-column-title text-break">Users</span>
        <div className="d-flex align-items-center">
          <ImageComponent
            filePath={userId ? `${rowData?.items?.tenant_id}/${userId}/profile_image` : null}
            fileName={rowData?.items?.first_name}
            fileSize="small.jpg"
          />

          <span
            className="text-nowrap"
            title={generateFullName(rowData?.items) ? generateFullName(rowData?.items) : rowData?.items?.user_name || undefined}
          >
            {rowData?.items?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
            {generateFullName(rowData?.items)
              ? truncateText(generateFullName(rowData?.items), 15)
              : truncateText(rowData?.items?.user_name, 15) || 'N/A'}
          </span>
        </div>
      </>
    );
  };

  const getContact = (rowData, contact) => {
    let url = '';
    let stateToPass = '';

    if (rowData?.type === 'Contact') {
      rowData?.items?.contact_id && (url = `/contacts/${rowData?.items?.contact_id}`);
      stateToPass = rowData;
    } else if (rowData?.type === 'Files') {
      if (rowData?.items?.file_id) {
        url = `/cases/${rowData?.items?.case_id}/files?file_id=${rowData?.items?.file_id}`;
      }
      stateToPass = rowData;
    } else if (rowData?.type === 'Lead' || rowData?.items?.is_lead === true) {
      url = `/leads/${rowData?.items?.case_id}`;
      stateToPass = rowData;
    }

    if (rowData?.type === 'Contact' && rowData?.items?.contact_id) {
      return (
        <Link to={url} state={stateToPass} className={'redirection-link-no-text-transform'} title={contact}>
          {' : ' + contact}
        </Link>
      );
    } else if (rowData?.type === 'Files' && rowData?.items?.entry_type !== 'folder') {
      return (
        <Link to={url} state={stateToPass} className={'redirection-link-no-text-transform'} title={contact}>
          {rowData?.items?.display_name ? ` : ${rowData?.items?.display_name}` : contact}
        </Link>
      );
    } else if (rowData?.type === 'Lead') {
      return (
        <Link to={url} state={stateToPass} className={'redirection-link-no-text-transform'} title={contact}>
          {contact}
        </Link>
      );
    } else {
      return <span>{contact ? ' : ' + contact : null}</span>;
    }
  };
  const changesTemplate = (rowData) => {
    if (!rowData?.message)
      return (
        <>
          <span className="p-column-title text-break">Description</span>
          <div className="ellipsis-text">N/A</div>
        </>
      );

    const { message: originalMessage, items } = rowData;
    let message = originalMessage;
    let contact = '';

    if ((items?.display_name && items?.file_id) || items?.contact_id) {
      const [firstPart, ...rest] = originalMessage.split(':');
      message = firstPart.replace(items.display_name, '').trim();
      contact = rest.join(':').trim();
    }

    const trimColon = (str) => (str?.endsWith?.(':') ? str?.slice(0, -1) : str);

    return (
      <>
        <span className="p-column-title text-break">Description</span>
        <div className="ellipsis-text" title={originalMessage}>
          {trimColon(message?.trim())?.endsWith?.(':') ? trimColon(message?.trim()).slice(0, -1) : trimColon(message?.trim())}
          {trimColon(getContact(rowData, contact))}
        </div>
      </>
    );
  };

  const caseLeadTemplate = (rowData) => {
    let url = '';
    let stateToPass = '';
    const caseId = rowData?.items?.case_id;

    //TODO:We need to add "is_lead"/"event_type" key to distinguish between lead and case moudle . Add this key when it is available from BE

    // Handle older data cases based on `type` and `is_lead`
    if ((rowData?.type?.toLowerCase() === 'lead' || rowData?.items?.is_lead === true) && caseId) {
      // Redirect to lead if type is 'lead' or `is_lead` is true and `case_id` exists
      url = `/leads/${caseId}`;
      stateToPass = rowData;
    } else if ((rowData?.type?.toLowerCase() === 'case' || rowData?.items?.is_lead === false || rowData?.is_lead === undefined) && caseId) {
      // Redirect to case if type is 'case' or `is_lead` is false or not present and `case_id` exists
      url = `/cases/${caseId}`;
      stateToPass = rowData;
    } else if (rowData?.items?.is_lead === true && !caseId) {
      // Redirect to lead if `is_lead` is true but no `case_id` exists
      url = '/leads';
      stateToPass = rowData;
    } else if (rowData?.items?.is_lead === false && !caseId) {
      // Redirect to case if `is_lead` is false or not present and no `case_id` exists
      url = '/cases';
      stateToPass = rowData;
    }

    const isRediretionLink = Boolean(rowData?.items?.case_name);

    return isRediretionLink ? (
      <>
        <span className="p-column-title text-break">Case/Lead Name</span>
        <div className="activity-log-ellipsis-text">
          <Link
            to={() => {
              if (rowData?.items?.case_name) {
                return url;
              }
            }}
            state={stateToPass}
            className={'redirection-link-no-text-transform text-decoration-none'}
            title={rowData?.items?.case_name ? rowData?.items?.case_name : undefined}
          >
            {rowData?.items?.case_name ? rowData?.items?.case_name : 'N/A'}
          </Link>
        </div>
      </>
    ) : (
      <>
        <span className="p-column-title text-break">Case/Lead Name</span>
        <div className="activity-log-ellipsis-text">{emptyDataValue}</div>
      </>
    );
  };

  function getFilePathForPagination() {
    if (isFromTeamManagement) {
      return { module: 'settings', subModule: 'team_management', listName: 'self' };
    }
    if (isLead && userContext && isCaseSpecific) {
      return { module: 'leads', subModule: '', listName: 'activity_log' };
    }
    if (isCaseSpecific && userContext && !isLead) {
      return { module: 'case', subModule: '', listName: 'activity_log' };
    }
    if (isFromDashBoard) {
      return { module: 'dashboard', subModule: '', listName: 'activity_log' };
    }
    if (isFromReports) {
      return { module: 'report', subModule: '', listName: 'activity_log' };
    }
    return { module: 'report', subModule: '', listName: 'activity_log' };
  }

  function pageOnChange(event) {
    if (onPageLimitChange) {
      onPageLimitChange(event);
      return;
    }
    const path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, event.value);
  }

  const defaultPaginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={pageOnChange} />
        </>
      );
    },
  };

  return (
    <div className="datatable-responsive overflow-auto">
      <ResizableDataTable
        value={activityLog}
        responsiveLayout="scroll"
        className="p-datatable-responsive"
        emptyMessage="No Data Found."
        paginatorTemplate={paginatorTemplateProp ?? defaultPaginatorTemplate}
        rows={rowsPerPage}
        stripedRows
        paginator
        rowsPerPageOptions={[10, 20, 50, 100]}
        sortField="timestamp"
        sortOrder={-1}
        columns={[
          {
            field: 'timestamp',
            header: 'Date And Time',
            body: dateTimeTemplate,
            sortable: true,
            responsiveLayout: 'scroll',
            bodyClassName: 'ellipsis-text',
          },
          { field: 'message', header: 'Description', body: changesTemplate, sortable: true, bodyClassName: 'ellipsis-text' },
          {
            field: 'items.case_name',
            header: 'Case/Lead Name',
            body: caseLeadTemplate,
            bodyClassName: 'ellipsis-text',
            hidden: isCaseSpecific,
          },
          { field: 'items.user_name', header: 'Users', body: userTemplate, sortable: true, bodyClassName: 'ellipsis-text' },
        ].filter((val) => val)}
        storageKey="activity_log_table_column_widths"
      />
    </div>
  );
}

export default ActivityLogTable;
